import { Component, Input, OnInit } from '@angular/core';
import { DocumentsService } from '../../services/documents.service';
import { PhotoViewerService } from '../../services/photo-viewer.service';
import {
  WorkDocument,
  WorkProjectdossierContentGroupDto,
  WorkProjectdossierDto,
  WorkProjectdossiersV2Service,
  WorkProjectdossierSourceEnum,
} from '../../_generated/work-rest-api';

/** Ein Dokument mit vorberechneter Darstellung — hält Logik aus dem Template heraus. */
interface DossierDocumentEntry {
  document: WorkDocument;
  title: string;
  isImage: boolean;
}

/** Eine Gruppe mit vorberechneter Überschrift. */
interface DossierGroupEntry {
  title: string;
  documents: DossierDocumentEntry[];
}

/** Ein Dossier mit seinem — erst beim Aufklappen geladenen — Inhalt. */
interface DossierCardEntry {
  dossier: WorkProjectdossierDto;
  groups: DossierGroupEntry[];
  isOpen: boolean;
  isLoading: boolean;
  isLoaded: boolean;
}

/**
 * Projektdossiers eines Projekts, ein aufklappbarer Bereich pro Dossier.
 *
 * Ersetzt in den Projektdetails die frühere Karte "Dokumente" mit ihrer hartcodierten
 * Documenttype-Whitelist: welche Dokumente ein Monteur oder Elektriker braucht, wird jetzt pro
 * Containertyp als Projektdossier-Vorlage konfiguriert.
 *
 * Die Rollenfilterung passiert serverseitig — die Komponente rendert genau, was sie bekommt, und
 * enthält bewusst keine Rollenlogik.
 *
 * Fotos bleiben aussen vor: die sind über "Fotos/Videos anzeigen" (project-photos) schon
 * erreichbar und würden hier nur die Liste zulaufen lassen.
 */
@Component({
  selector: 'ax-project-dossiers',
  templateUrl: './ax-project-dossiers.component.html',
})
export class AxProjectDossiersComponent implements OnInit {
  @Input() projectId: number;
  public entries: DossierCardEntry[] = [];

  constructor(
    private readonly projectdossiersService: WorkProjectdossiersV2Service,
    private readonly documentsService: DocumentsService,
    private readonly photoViewerService: PhotoViewerService,
  ) {
  }

  public ngOnInit() {
    this.projectdossiersService.projectdossiersV2ControllerFindByProjectId({ projectId: this.projectId }).subscribe({
      next: dossiers => {
        this.entries = dossiers.map(dossier => ({
          dossier,
          groups: [],
          isOpen: false,
          isLoading: false,
          isLoaded: false,
        }));
      },
    });
  }

  public toggleEntry(entry: DossierCardEntry): void {
    entry.isOpen = !entry.isOpen;
    if (entry.isOpen) {
      this.loadContent(entry);
    }
  }

  /** Inhalt pro Dossier lazy laden — der Artikeldokument-Zweig ist serverseitig teuer. */
  public loadContent(entry: DossierCardEntry): void {
    if (entry.isLoaded || entry.isLoading) {
      return;
    }
    entry.isLoading = true;
    // Individuelle Dossiers gehören zu keiner Vorlage und werden über ihre Instanz-Id adressiert.
    const content$ = entry.dossier.isIndividual
      ? this.projectdossiersService.projectdossiersV2ControllerGetIndividualContent({
        projectId: this.projectId,
        projectdossierId: entry.dossier.projectdossierId,
      })
      : this.projectdossiersService.projectdossiersV2ControllerGetContent({
        projectId: this.projectId,
        templateId: entry.dossier.projectdossiertemplateId,
      });
    content$.subscribe({
      next: content => {
        entry.groups = (content.groups || [])
          // Fotos kommen über die Fotogalerie, und leere Gruppen interessieren auf dem Bau nicht.
          .filter(group => group.source !== WorkProjectdossierSourceEnum.ProjectPhoto && group.documentsCount > 0)
          .map(group => ({
            title: AxProjectDossiersComponent.BUILD_GROUP_TITLE(group),
            documents: (group.documents || []).map(document => ({
              document,
              title: document.prettyName || document.name || 'Dokument',
              isImage: !!document.mimetype && document.mimetype.startsWith('image'),
            })),
          }));
        entry.isLoading = false;
        entry.isLoaded = true;
      },
      error: () => {
        entry.isLoading = false;
      },
    });
  }

  /** Bilder im PhotoViewer, alles andere im PDF-Modal. */
  public async openDocument(entry: DossierDocumentEntry): Promise<void> {
    if (entry.isImage) {
      await this.photoViewerService.openAxovaDocumentImage(entry.document);
      return;
    }
    await this.documentsService.openPdf(entry.document);
  }

  /** Subtyp bzw. Artikel/Energieversorger konkretisieren den Dokumenttyp in der Überschrift. */
  private static BUILD_GROUP_TITLE(group: WorkProjectdossierContentGroupDto): string {
    const parts: string[] = [group.documenttypeName];
    if (group.documentsubtypeName) {
      parts.push(group.documentsubtypeName);
    }
    if (group.itemName) {
      parts.push(group.itemName);
    }
    if (group.supplierName) {
      parts.push(group.supplierName);
    }
    return parts.join(' · ');
  }
}
